import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Field, Th, Td } from "./Customers";
import { Send, Mail, MessageCircle, AlertTriangle, CheckCircle2, X, Eye, Plus, Loader2 } from "lucide-react";

export default function Notifications() {
  const [providers, setProviders] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [outbox, setOutbox] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const [emailIn, setEmailIn] = useState("");
  const [waIn, setWaIn] = useState("");

  const reload = () => Promise.all([
    api.get("/notifications/providers").then(({ data }) => setProviders(data)),
    api.get("/notifications/config").then(({ data }) => setCfg(data)),
    api.get("/notifications/outbox").then(({ data }) => setOutbox(data)),
  ]);

  useEffect(() => { reload(); }, []);

  const update = async (patch) => {
    const { data } = await api.put("/notifications/config", patch);
    setCfg(data);
    setSavedAt(Date.now());
  };

  const addEmail = async () => {
    if (!emailIn.trim()) return;
    const list = Array.from(new Set([...(cfg.email_recipients || []), emailIn.trim()]));
    await update({ email_recipients: list });
    setEmailIn("");
  };
  const removeEmail = async (e) => update({ email_recipients: (cfg.email_recipients || []).filter((x) => x !== e) });
  const addWa = async () => {
    if (!waIn.trim()) return;
    const list = Array.from(new Set([...(cfg.whatsapp_recipients || []), waIn.trim()]));
    await update({ whatsapp_recipients: list });
    setWaIn("");
  };
  const removeWa = async (w) => update({ whatsapp_recipients: (cfg.whatsapp_recipients || []).filter((x) => x !== w) });
  const toggleChannel = async (ch) => {
    const has = (cfg.channels || []).includes(ch);
    const list = has ? cfg.channels.filter((x) => x !== ch) : [...(cfg.channels || []), ch];
    await update({ channels: list });
  };
  const toggleEnabled = async () => update({ enabled: !cfg.enabled });

  const sendNow = async () => {
    setErr(""); setSending(true);
    try { await api.post("/notifications/brief/send"); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const openPreview = async () => {
    const { data } = await api.get("/notifications/preview");
    setPreview(data);
  };

  if (!cfg || !providers) return <div className="p-8 text-sm text-[#495057]" data-testid="notifications-loading">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle="Auto-deliver the Gr8Agentic Morning Brief to your team via WhatsApp and email."
        action={
          <div className="flex gap-2">
            <button onClick={openPreview} data-testid="preview-brief-btn" className="inline-flex items-center gap-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] px-4 py-2.5 text-sm rounded-sm">
              <Eye size={14} /> Preview brief
            </button>
            <button onClick={sendNow} disabled={sending} data-testid="send-brief-btn"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white px-4 py-2.5 text-sm rounded-sm">
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send now</>}
            </button>
          </div>
        }
      />

      {/* Provider status banner */}
      <div className={`mt-8 border p-5 ${providers.test_mode ? "border-[#F5B700] bg-[#F5B700]/5" : "border-[#0B7A75] bg-[#0B7A75]/5"}`} data-testid="provider-status">
        <div className="flex items-start gap-3">
          {providers.test_mode ? <AlertTriangle size={18} className="text-[#F5B700] mt-0.5" /> : <CheckCircle2 size={18} className="text-[#0B7A75] mt-0.5" />}
          <div className="flex-1">
            <div className="font-heading font-semibold tracking-tight">
              {providers.test_mode ? "Test mode — messages are queued, not sent" : "Live delivery active"}
            </div>
            <div className="text-sm text-[#495057] mt-1">
              {providers.test_mode
                ? "Provider keys aren't configured yet. The brief renderer is fully working and every send is captured in the Outbox below so you can preview content. Add Twilio + Resend keys in /app/backend/.env (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_WHATSAPP_FROM, RESEND_API_KEY, RESEND_FROM) and restart the backend to flip live."
                : `Email via ${providers.providers.email}. WhatsApp via ${providers.providers.whatsapp}.`}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Email config */}
        <div className="border border-[#0A0A0A] bg-white" data-testid="email-config">
          <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
            <div className="flex items-center gap-2"><Mail size={16} /><span className="font-heading font-semibold tracking-tight">Email</span></div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={(cfg.channels || []).includes("email")} onChange={() => toggleChannel("email")} data-testid="toggle-email" />
              Enabled
            </label>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <input value={emailIn} onChange={(e) => setEmailIn(e.target.value)} type="email" placeholder="recipient@company.com"
                data-testid="add-email-input"
                className="flex-1 bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none" />
              <button onClick={addEmail} data-testid="add-email-btn" className="px-3 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white text-sm rounded-sm"><Plus size={14} /></button>
            </div>
            <div className="space-y-1">
              {(cfg.email_recipients || []).length === 0 && <div className="text-sm text-[#495057]">No email recipients yet.</div>}
              {(cfg.email_recipients || []).map((e, i) => (
                <div key={e} data-testid={`email-recip-${i}`} className="flex items-center justify-between px-3 py-2 border border-[#DEE2E6] bg-[#F8F9FA]">
                  <span className="text-sm font-mono">{e}</span>
                  <button onClick={() => removeEmail(e)} data-testid={`email-recip-remove-${i}`} className="text-[#D90429] p-1"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* WhatsApp config */}
        <div className="border border-[#0A0A0A] bg-white" data-testid="whatsapp-config">
          <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
            <div className="flex items-center gap-2"><MessageCircle size={16} /><span className="font-heading font-semibold tracking-tight">WhatsApp</span></div>
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={(cfg.channels || []).includes("whatsapp")} onChange={() => toggleChannel("whatsapp")} data-testid="toggle-whatsapp" />
              Enabled
            </label>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex gap-2">
              <input value={waIn} onChange={(e) => setWaIn(e.target.value)} placeholder="+233241234567 (E.164)"
                data-testid="add-wa-input"
                className="flex-1 bg-white border border-[#DEE2E6] px-3 py-2 text-sm focus:border-[#0A0A0A] focus:outline-none" />
              <button onClick={addWa} data-testid="add-wa-btn" className="px-3 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white text-sm rounded-sm"><Plus size={14} /></button>
            </div>
            <div className="space-y-1">
              {(cfg.whatsapp_recipients || []).length === 0 && <div className="text-sm text-[#495057]">No WhatsApp numbers yet.</div>}
              {(cfg.whatsapp_recipients || []).map((w, i) => (
                <div key={w} data-testid={`wa-recip-${i}`} className="flex items-center justify-between px-3 py-2 border border-[#DEE2E6] bg-[#F8F9FA]">
                  <span className="text-sm font-mono">{w}</span>
                  <button onClick={() => removeWa(w)} data-testid={`wa-recip-remove-${i}`} className="text-[#D90429] p-1"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-[#495057]">Tip: in Twilio Sandbox, recipients must first text the &ldquo;join code&rdquo; to the sandbox number.</div>
          </div>
        </div>
      </div>

      {/* Master switch */}
      <div className="mt-6 flex items-center gap-3 text-sm">
        <label className="inline-flex items-center gap-2" data-testid="master-toggle-label">
          <input type="checkbox" checked={!!cfg.enabled} onChange={toggleEnabled} data-testid="master-toggle" />
          <span className="font-medium">Delivery enabled</span>
        </label>
        {savedAt && <span className="text-xs text-[#0B7A75]">Saved {new Date(savedAt).toLocaleTimeString()}</span>}
        {err && <span className="text-xs text-[#D90429]">{err}</span>}
      </div>

      <ScheduleEditor />

      {/* Outbox */}
      <div className="mt-10">
        <h3 className="font-heading text-xl font-semibold tracking-tight mb-3">Outbox · last 50</h3>
        <div className="border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>When</Th><Th>Channel</Th><Th>To</Th><Th>Subject</Th><Th>Status</Th></tr>
            </thead>
            <tbody>
              {outbox.map((o, i) => (
                <tr key={o.id} data-testid={`outbox-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                  <Td className="text-[#495057] text-xs">{new Date(o.created_at).toLocaleString()}</Td>
                  <Td>{o.channel === "email" ? <Mail size={12} className="inline mr-1" /> : <MessageCircle size={12} className="inline mr-1" />}{o.channel}</Td>
                  <Td className="font-mono text-xs">{o.to}</Td>
                  <Td className="text-[#495057] truncate max-w-[200px]">{o.subject || "—"}</Td>
                  <Td><OutboxStatus s={o.status} /></Td>
                </tr>
              ))}
              {outbox.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">Outbox is empty. Click &ldquo;Send now&rdquo; above to test.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {preview && <PreviewModal preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function OutboxStatus({ s }) {
  const cls = {
    sent: "border-[#0B7A75] bg-[#0B7A75]/10 text-[#0B7A75]",
    queued_test: "border-[#F5B700] bg-[#F5B700]/10 text-[#0A0A0A]",
    failed: "border-[#D90429] bg-[#D90429]/10 text-[#D90429]",
  }[s] || "";
  return <span data-testid={`outbox-status-${s}`} className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${cls}`}>{s.replace("_", " ")}</span>;
}

function ScheduleEditor() {
  const [sch, setSch] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const load = () => Promise.all([
    api.get("/notifications/schedule").then(({ data }) => setSch(data)),
    api.get("/notifications/schedule/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs([])),
  ]);
  useEffect(() => { load(); }, []);
  if (!sch) return null;
  const save = async (patch) => {
    setErr("");
    const next = { ...sch, ...patch };
    try { await api.put("/notifications/schedule", next); setSch(next); setSavedAt(Date.now()); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="mt-8 border border-[#0A0A0A] bg-white" data-testid="schedule-editor">
      <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
        <div className="font-heading font-semibold tracking-tight inline-flex items-center gap-2">
          <Send size={14} /> Daily auto-delivery schedule
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!sch.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="schedule-enabled" />
          Enabled
        </label>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Hour (0-23)</span>
          <input type="number" min="0" max="23" value={sch.hour} onChange={(e) => save({ hour: parseInt(e.target.value || 0) })}
            data-testid="schedule-hour" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Minute</span>
          <input type="number" min="0" max="59" value={sch.minute} onChange={(e) => save({ minute: parseInt(e.target.value || 0) })}
            data-testid="schedule-minute" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Days (cron)</span>
          <input value={sch.days_of_week} onChange={(e) => save({ days_of_week: e.target.value })}
            placeholder="mon-fri"
            data-testid="schedule-days" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Timezone</span>
          <input value={sch.timezone} onChange={(e) => save({ timezone: e.target.value })}
            placeholder="Africa/Accra"
            data-testid="schedule-timezone" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm" />
        </label>
      </div>
      <div className="px-5 pb-4 text-xs text-[#495057]">
        {jobs.length > 0 ? (
          <span data-testid="schedule-next-run">Next run: <strong className="text-[#0A0A0A]">{jobs[0].next_run ? new Date(jobs[0].next_run).toLocaleString() : "—"}</strong></span>
        ) : sch.enabled ? "Schedule saved but no job registered (set recipients first)." : "Schedule disabled."}
        {savedAt && <span className="text-[#0B7A75] ml-3">✓ Saved {new Date(savedAt).toLocaleTimeString()}</span>}
        {err && <span className="text-[#D90429] ml-3">{err}</span>}
      </div>
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  const [tab, setTab] = useState("email");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="preview-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-[#0A0A0A] max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-[#0A0A0A] flex items-center justify-between">
          <div className="font-heading font-semibold tracking-tight">Brief preview</div>
          <button onClick={onClose} className="p-1" data-testid="preview-close"><X size={16} /></button>
        </div>
        <div className="flex gap-2 px-5 pt-3 border-b border-[#DEE2E6]">
          <button onClick={() => setTab("email")} data-testid="preview-tab-email"
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "email" ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057]"}`}>Email</button>
          <button onClick={() => setTab("whatsapp")} data-testid="preview-tab-whatsapp"
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "whatsapp" ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057]"}`}>WhatsApp</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "email" ? (
            <iframe data-testid="preview-email-iframe" srcDoc={preview.email_html} title="email preview" className="w-full h-[60vh] border border-[#DEE2E6]" />
          ) : (
            <pre data-testid="preview-whatsapp-text" className="whitespace-pre-wrap font-mono text-sm bg-[#F8F9FA] border border-[#DEE2E6] p-4 max-w-md mx-auto">{preview.whatsapp_text}</pre>
          )}
        </div>
      </div>
    </div>
  );
}
